import { forwardRef } from 'react';
import type { PropsWithoutRef, ReactNode, Ref } from 'react';

import Link from '../Link';
import type { BaseProps, ForwardRefForwardPropsComponent, NoChildrenProp } from '../../types';
import QRCode from '../QRCode';
import NoValue from '../NoValue/NoValue';

export interface URLDisplayProps extends BaseProps, NoChildrenProp {
  /** URL address, includes scheme, 'http://', 'https://' */
  value?: string;
  /** The text to be displayed. */
  displayText?: string;
  /**
   * Controls styling of the field.
   * @default 'link'
   */
  variant?: 'link' | 'text' | 'qrcode';
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLAnchorElement | HTMLSpanElement | HTMLImageElement | HTMLDivElement>;
}

/**
 * Function gets the url as a string and converts it to a HTML markup with whitespace opportunity elements.
 * It helps breaking a long url correctly to avoid parent container overflow issues
 * according to "The Chicago Manual of Style" copywriting standard.
 * @param url input url to construct markup for
 * @returns array of nodes
 */
const generateUrlMarkup = (url: string): ReactNode => {
  const markedUrl = url
    .split('//')
    // Insert \x1f (unit separator) char as a placeholder at correct position.
    .map(str => {
      return str
        .replace(/:/giu, '$&\x1f')
        .replace(/[/~.,\\-_?#%]/giu, '\x1f$&')
        .replace(/[=&]/giu, '\x1f$&\x1f');
    })
    .join('//\x1f');

  // Replace unit separator character with <wbr /> element. Create an array of nodes in place of string to pass tags escaping.
  return markedUrl.split('\x1f').reduce<ReactNode[]>((urlFragments, part) => {
    urlFragments.push(part, <wbr key={urlFragments.length} />);
    return urlFragments;
  }, []);
};

const URLDisplay: ForwardRefForwardPropsComponent<URLDisplayProps> = forwardRef(function URLDisplay(
  { value, displayText, variant = 'link', ...restProps }: PropsWithoutRef<URLDisplayProps>,
  ref: URLDisplayProps['ref']
) {
  const displayValue = value ? displayText || generateUrlMarkup(value) : <NoValue />;

  if (variant === 'text' || !value) {
    return (
      <span ref={ref as Ref<HTMLSpanElement>} {...restProps}>
        {displayValue}
      </span>
    );
  }
  if (variant === 'qrcode') {
    return (
      <QRCode ref={ref as Ref<HTMLImageElement>} {...restProps} value={value} label={displayText} />
    );
  }
  return (
    <Link ref={ref as Ref<HTMLAnchorElement>} {...restProps} href={value}>
      {displayValue}
    </Link>
  );
});

export default URLDisplay;
